import React, { useEffect, useState, useRef, useCallback } from "react";
import Markdown from "markdown-to-jsx";
import logo from "../img/official_logo.svg";
import logoDark from "../img/official_logo_dark.svg";
import "../styles/Docs.css";

// Chapter files in order — update this list if chapters are added
const CHAPTERS = [
  "01-introduction.md",
  "02-getting-started.md",
  "03-search.md",
  "04-results.md",
  "05-evidence-chains.md",
  "06-ai-assistant.md",
  "07-account.md",
];

// Parse YAML frontmatter, returning { meta, body }
const parseFrontmatter = (text) => {
  if (text.startsWith("---")) {
    const end = text.indexOf("\n---", 3);
    if (end !== -1) {
      const yamlBlock = text.slice(4, end);
      const afterClose = text.indexOf("\n", end + 1);
      const body = afterClose !== -1 ? text.slice(afterClose + 1).trimStart() : "";
      const meta = {};
      yamlBlock.split("\n").forEach((line) => {
        const colon = line.indexOf(":");
        if (colon !== -1) {
          const key = line.slice(0, colon).trim();
          const val = line.slice(colon + 1).trim().replace(/^["']|["']$/g, "");
          meta[key] = val === "null" ? null : val;
        }
      });
      return { meta, body };
    }
  }
  return { meta: {}, body: text };
};

const DocsPage = () => {
  const [sections, setSections] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const contentRef = useRef(null);
  const observerRef = useRef(null);
  const isDark = document.documentElement.getAttribute("data-theme") === "dark";

  useEffect(() => {
    const fetchChapters = async () => {
      try {
        const texts = await Promise.all(
          CHAPTERS.map((ch) =>
            fetch(`/docs/user-manual/${ch}`).then((r) => {
              if (!r.ok) throw new Error(`Failed to load ${ch}: ${r.status}`);
              return r.text();
            })
          )
        );
        const parsed = texts.map(parseFrontmatter);
        setSections(parsed);
        if (parsed.length) setActiveSection(parsed[0].meta["section-id"]);
      } catch (e) {
        setError(e.message);
      }
    };
    fetchChapters();
  }, []);

  useEffect(() => {
    if (!sections || !contentRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: contentRef.current,
        rootMargin: "-10% 0px -80% 0px",
        threshold: 0,
      }
    );

    sections.forEach(({ meta }) => {
      const id = meta["section-id"];
      if (id) {
        const el = document.getElementById(id);
        if (el) observerRef.current.observe(el);
      }
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (error) {
    return <div className="docs-page-error">Failed to load documentation: {error}</div>;
  }

  if (!sections) {
    return <div className="docs-page-loading">Loading documentation…</div>;
  }

  return (
    <div className="docs-page-wrapper">
      <header className="doc-header docs-page-header">
        <a href="/" className="doc-logo-link" title="Back to Plex Search">
          <img src={isDark ? logoDark : logo} alt="Plex Research" className="doc-logo" />
        </a>
        <span className="doc-header-title">User Manual</span>
        <a href="/" className="doc-back-link">Back to Plex Search</a>
      </header>
      <div className="docs-layout">
        <aside className="doc-sidebar">
          <nav className="doc-toc">
            {sections.map(({ meta }) => {
              const id = meta["section-id"];
              const title = meta["title"];
              if (!id || !title) return null;
              return (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeSection === id ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(id);
                  }}
                >
                  {title}
                </a>
              );
            })}
          </nav>
        </aside>
        <main className="docs-page" ref={contentRef}>
          {sections.map(({ meta, body }, i) => (
            <section key={CHAPTERS[i]} id={meta["section-id"]}>
              <Markdown>{body}</Markdown>
              {i < sections.length - 1 && <hr />}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default DocsPage;